import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { policyFeaturesApi } from '@/services/policy-features'
import { buildEffectivePost } from '@/lib/effective-capabilities'
import { evaluateRouteAccess } from '@/lib/route-access'
import { resolveActiveServiceInstanceId } from '@/lib/service-instance'
import { useAuthStore, type Post } from '@/stores/auth-store'

const resolveCurrentRoleId = (currentRole: Post | null | undefined) => (
  currentRole?.role_id || currentRole?.roles_data?.id || null
)

const resolveCurrentRoleCacheKey = (currentRole: Post | null | undefined) => (
  currentRole?.roles_data?.unique_id || resolveCurrentRoleId(currentRole)
)

export function useEffectivePostAccess(sourcePost?: Post | null | undefined) {
  const location = useLocation()
  const currentPost = useAuthStore((state) => state.currentPost)
  const currentBranch = useAuthStore((state) => state.currentBranch)
  const availableBranches = useAuthStore((state) => state.availableBranches)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const post = sourcePost ?? currentPost

  const serviceInstanceId = resolveActiveServiceInstanceId({
    search: location.search,
    post,
    currentBranch,
    availableBranches,
    storedServiceInstanceId,
  })

  const roleId = resolveCurrentRoleId(post)
  const roleCacheKey = resolveCurrentRoleCacheKey(post)

  const policyFeaturesQuery = useQuery({
    queryKey: ['sidebar-policy-features', roleCacheKey, serviceInstanceId],
    queryFn: () => policyFeaturesApi.listCurrentService({
      roleId: roleId!,
      serviceInstanceId: serviceInstanceId!,
      includeHiddenFeatures: true,
    }),
    enabled: Boolean(roleId && roleCacheKey && serviceInstanceId && post),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const effectivePost = useMemo(
    () => buildEffectivePost(post, policyFeaturesQuery.data?.features),
    [post, policyFeaturesQuery.data?.features],
  )

  const canAccessPath = useMemo(
    () => (pathname: string) => evaluateRouteAccess(pathname.split('?')[0], effectivePost).authorized,
    [effectivePost],
  )

  return {
    effectivePost,
    effectiveCapabilities: effectivePost?.capabilities,
    policyFeatures: policyFeaturesQuery.data?.features,
    isPolicyFeaturesLoading: policyFeaturesQuery.isLoading,
    canAccessPath,
  }
}
